import React, { useEffect } from 'react';
import { EnvironmentContext, MetricsContextProvider } from '../context';
import { type UseExtensionQueryHookOptions } from '../web-client';
import {
	type AnalyticsWebClient,
	ForgeUIAnalyticsListener,
	sendEvent,
	type GasV3Payload,
} from '../analytics';
import { type ProductEnvironment } from '@atlassian/forge-ui-types';
import { getProductDomain } from '../custom-ui/bridge';

export type ForgeUIExtensionQueryOptions = UseExtensionQueryHookOptions;

let postRobotOn: any = null;
// bridge-core import relies on window object. It is not available in SSR.
if (typeof window !== 'undefined') {
	postRobotOn = require('@atlassian/bridge-core').postRobotOn;
}

export const ForgeUIExtensionPointProvider = ({
	analyticsWebClient,
	environment,
	product,
	page,
	children,
	localId,
	isCustomConfig,
}: {
	analyticsWebClient: AnalyticsWebClient | Promise<AnalyticsWebClient>;
	children: React.ReactNode;
	environment: ProductEnvironment;
	isCustomConfig?: boolean;
	localId?: string;
	page: string;
	product: string;
}): React.JSX.Element => {
	// Listen for analytics events from child iframes (FAUS) - Confluence only
	// TODO: this is a potential target for refactoring, we should avoid product / module specific concerns
	// in the shared component if possible.
	useEffect(() => {
		if (product === 'confluence' && localId && postRobotOn && !isCustomConfig) {
			try {
				const allowedDomain = getProductDomain();
				const listener = postRobotOn(
					`analyticsFromEmbeddedFAUS_${localId}`,
					{ domain: allowedDomain },
					async ({ data }: { data: GasV3Payload }) => {
						// Forward to the product's analytics client using the existing sendEvent utility
						sendEvent(analyticsWebClient)(data);
					},
				);
				return () => listener.cancel();
			} catch {
				// eslint-disable-next-line no-console
				console.log('Failed to set up analytics listener');
			}
		}
	}, [analyticsWebClient, localId, isCustomConfig, product]);

	return (
		<EnvironmentContext.Provider value={environment}>
			<MetricsContextProvider value={{ product, page }}>
				<ForgeUIAnalyticsListener
					client={analyticsWebClient}
					commonAttributes={{
						moduleType: page,
					}}
				>
					{children}
				</ForgeUIAnalyticsListener>
			</MetricsContextProvider>
		</EnvironmentContext.Provider>
	);
};
